import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSEO from "@/hooks/useSEO";
import { BreadcrumbSchema } from "@/components/SEOStructuredData";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import usePageContent from "@/hooks/usePageContent";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import SkipToContent from "@/components/SkipToContent";
import ScrollReveal from "@/components/ScrollReveal";
import TalentApplicationForm from "@/components/TalentApplicationForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Users, Globe, Mic, Award, Calendar } from "lucide-react";

const ForTalent = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || "en";
  const { localizedPath } = useLocalizedPath();
  const { getContent, isSectionHidden } = usePageContent("for-talent");

  useEffect(() => {
    document.body.classList.add("show-recaptcha-badge");
    return () => document.body.classList.remove("show-recaptcha-badge");
  }, []);

  useSEO({
    title: getContent("hero_title", t("forTalent.pageTitle")),
    description: getContent("intro", t("forTalent.pageDescription")),
  });

  // Helper to strip HTML tags (and also handle cases where tags were stored as escaped entities)
  const stripHtml = (input: string) => {
    const firstPass = new DOMParser().parseFromString(input, "text/html").body.textContent || "";
    const secondPass = new DOMParser().parseFromString(firstPass, "text/html").body.textContent || "";
    return secondPass;
  };

  const breadcrumbs = [
    { name: currentLang === "fr" ? "Accueil" : "Home", url: `https://firststagetalent.com/${currentLang}` },
    { name: stripHtml(getContent("hero_title", t("forTalent.pageTitle"))), url: `https://firststagetalent.com/${currentLang}/for-talent` },
  ];

  // Parse criteria items from newline-separated text
  const criteriaText = getContent("criteria_items", "");
  const criteria = criteriaText
    ? criteriaText
        .split("\n")
        .map((item) => stripHtml(item))
        .filter((item) => item.trim())
    : [];

  // Build benefits array from individual CMS fields
  const benefits = [
    { title: getContent("benefit1_title", ""), description: getContent("benefit1_desc", "") },
    { title: getContent("benefit2_title", ""), description: getContent("benefit2_desc", "") },
    { title: getContent("benefit3_title", ""), description: getContent("benefit3_desc", "") },
    { title: getContent("benefit4_title", ""), description: getContent("benefit4_desc", "") },
    { title: getContent("benefit5_title", ""), description: getContent("benefit5_desc", "") },
    { title: getContent("benefit6_title", ""), description: getContent("benefit6_desc", "") },
  ].filter(b => b.title && b.description);

  const benefitIcons = [Users, Globe, Mic, Award, Calendar, CheckCircle];

  return (
    <div className="min-h-screen">
      <BreadcrumbSchema items={breadcrumbs} />
      <SkipToContent />
      <Header />
      <main id="main-content" tabIndex={-1} className="outline-none">
        <PageHero 
          title={stripHtml(getContent("hero_title", t("forTalent.heroTitle")))} 
          subtitle={stripHtml(getContent("hero_subtitle", t("forTalent.heroSubtitle")))}
        />

        {/* Introduction */}
        <section className="section-padding">
          <div className="container-wide max-w-4xl mx-auto text-center">
            <ScrollReveal>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                {stripHtml(getContent("intro", t("forTalent.intro")))}
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Criteria Section */}
        {!isSectionHidden("criteria_title") && (
          <section className="section-padding bg-muted/30">
            <div className="container-wide">
              <ScrollReveal>
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium text-center mb-4 text-primary">
                  {stripHtml(getContent("criteria_title", t("forTalent.criteria.title")))}
                </h2>
                <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                  {stripHtml(getContent("criteria_subtitle", t("forTalent.criteria.subtitle")))}
                </p>
              </ScrollReveal>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                {criteria.map((item, index) => (
                  <ScrollReveal key={index} delay={index * 0.1} className="h-full">
                    <div className="flex items-start gap-4 p-6 bg-background rounded-lg border border-border h-full">
                      <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-primary">{item}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* What We Offer Section */}
        {!isSectionHidden("benefits_title") && (
          <section className="section-padding">
            <div className="container-wide">
              <ScrollReveal>
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-medium text-center mb-4 text-primary">
                  {stripHtml(getContent("benefits_title", t("forTalent.benefits.title")))}
                </h2>
                <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                  {stripHtml(getContent("benefits_subtitle", t("forTalent.benefits.subtitle")))}
                </p>
              </ScrollReveal>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => {
                  const Icon = benefitIcons[index % benefitIcons.length];
                  return (
                    <ScrollReveal key={index} delay={index * 0.1}>
                      <Card className="h-full border-border hover:border-primary/30 transition-colors">
                        <CardContent className="p-6">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <h3 className="font-display text-lg font-medium mb-2 text-primary">
                            {stripHtml(benefit.title)}
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            {stripHtml(benefit.description)}
                          </p>
                        </CardContent>
                      </Card>
                    </ScrollReveal>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Application Form */}
        <TalentApplicationForm 
          title={stripHtml(getContent("form_title", ""))}
          subtitle={stripHtml(getContent("form_subtitle", ""))}
        />
      </main>
      <Footer />
    </div>
  );
};

export default ForTalent;
